/**
 * Soft guards for starting a session, surfaced as a WARNING the staff member
 * can override (unlike the empty-class block, which the backend enforces hard).
 *
 * Two cases, both "usually wrong but sometimes intended":
 *  - Wrong date: the session is for a day that isn't today. Backfilling a
 *    missed day is legitimate, so we warn (strongly, naming both dates) rather
 *    than block — the events are still filed under the session's real date.
 *  - Off-time: right day, but more than ±15 min from the scheduled start
 *    (arriving early, or catching up late). Real-world timing wobble is normal,
 *    so ±15 min is silent and beyond it just warns.
 *
 * `todayIso` / `nowHHMM` are injected so this stays pure and testable.
 */
const TIME_BUFFER_MINUTES = 15;

function toMinutes(hhmm: string): number | null {
  const m = /^(\d{2}):(\d{2})$/.exec(hhmm);
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

export function sessionStartWarning(
  sessionDateIso: string,
  scheduledStartHHMM: string | undefined,
  todayIso: string,
  nowHHMM: string
): string | null {
  // Wrong date wins — it's the more serious of the two, and off-time is
  // meaningless when you're not even on today's session.
  if (sessionDateIso !== todayIso) {
    return `This session is for ${sessionDateIso}, not today (${todayIso}). Attendance will be recorded under ${sessionDateIso}. Start anyway?`;
  }

  const sched = scheduledStartHHMM ? toMinutes(scheduledStartHHMM) : null;
  const now = toMinutes(nowHHMM);
  if (sched !== null && now !== null && Math.abs(now - sched) > TIME_BUFFER_MINUTES) {
    const early = now < sched;
    return `This class is scheduled to start at ${scheduledStartHHMM}, and it's ${nowHHMM} now — ${
      early ? 'starting early' : 'starting late'
    }. Start anyway?`;
  }

  return null;
}
